import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import { X, Code2 } from "lucide-react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-python";
import "prismjs/components/prism-java";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-go";
import "prismjs/themes/prism-tomorrow.css";

const LANG_OPTIONS = [
    { label: "JavaScript", value: "javascript" },
    { label: "Python",     value: "python" },
    { label: "Java",       value: "java" },
    { label: "SQL",        value: "sql" },
    { label: "Go",        value: "go" },
];

function CodeToggle({ active, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            title="Toggle code block"
            className={`ml-2 p-1 rounded transition ${active ? "text-indigo-400 bg-indigo-500/15" : "text-gray-500 hover:text-gray-300"}`}
        >
            <Code2 size={15} />
        </button>
    );
}
CodeToggle.propTypes = { active: PropTypes.bool.isRequired, onToggle: PropTypes.func.isRequired };

function CodeField({ value, onChange, lang, onLangChange }) {
    return (
        <div className="rounded-lg overflow-hidden border border-gray-700 focus-within:ring-2 focus-within:ring-indigo-500">
            <div className="flex items-center gap-2 bg-[#0d0f14] px-3 py-1.5 border-b border-gray-700">
                <Code2 size={12} className="text-gray-500" />
                <select
                    value={lang}
                    onChange={(e) => onLangChange(e.target.value)}
                    className="bg-transparent text-gray-400 text-xs focus:outline-none cursor-pointer"
                >
                    {LANG_OPTIONS.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                </select>
            </div>
            <Editor
                value={value}
                onValueChange={onChange}
                highlight={code => Prism.highlight(code, Prism.languages[lang] ?? Prism.languages.javascript, lang)}
                padding={12}
                spellCheck={false}
                style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 13,
                    background: "#0d0f14",
                    minHeight: "100px",
                    lineHeight: "1.6",
                    caretColor: "white",
                }}
            />
        </div>
    );
}
CodeField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, lang: PropTypes.string.isRequired, onLangChange: PropTypes.func.isRequired };

export default function AddRecallCardModal({
    onClose,
    onSubmit,
    initialFront = "",
    initialBack = "",
    initialHint = "",
    initialTags = "",
    isSubmitting = false,
    title = "Add Card",
}) {
    const [front, setFront] = useState(initialFront);
    const [back, setBack] = useState(initialBack);
    const [hint, setHint] = useState(initialHint);
    const [tags, setTags] = useState(initialTags);
    const [frontIsCode, setFrontIsCode] = useState(false);
    const [backIsCode, setBackIsCode]   = useState(false);
    const [frontLang, setFrontLang] = useState("javascript");
    const [backLang, setBackLang]   = useState("javascript");

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        setFront(initialFront);
        setBack(initialBack);
        setHint(initialHint);
        setTags(initialTags);
    }, [initialFront, initialBack, initialHint, initialTags]);

    const inputClass = "bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500";

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 px-4" onClick={handleBackdropClick}>
            <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl shadow-xl p-6 w-full max-w-md relative">

                <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <X size={20} />
                </button>

                <h2 className="text-xl font-semibold text-gray-100 mb-4">{title}</h2>

                <div className="flex flex-col gap-4">

                    {/* Front */}
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center">
                            <label htmlFor="card-front" className="text-gray-300 text-sm">Front</label>
                            <CodeToggle active={frontIsCode} onToggle={() => setFrontIsCode(v => !v)} />
                        </div>
                        {frontIsCode
                            ? <CodeField value={front} onChange={setFront} lang={frontLang} onLangChange={setFrontLang} />
                            : <textarea id="card-front" value={front} onChange={(e) => setFront(e.target.value)} className={`${inputClass} min-h-24 resize-y`} />
                        }
                    </div>

                    {/* Back */}
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center">
                            <label htmlFor="card-back" className="text-gray-300 text-sm">Back</label>
                            <CodeToggle active={backIsCode} onToggle={() => setBackIsCode(v => !v)} />
                        </div>
                        {backIsCode
                            ? <CodeField value={back} onChange={setBack} lang={backLang} onLangChange={setBackLang} />
                            : <textarea id="card-back" value={back} onChange={(e) => setBack(e.target.value)} className={`${inputClass} min-h-24 resize-y`} />
                        }
                    </div>

                    {/* Hint */}
                    <div className="flex flex-col gap-1">
                        <label htmlFor="card-hint" className="text-gray-300 text-sm">Hint</label>
                        <input id="card-hint" type="text" value={hint} onChange={(e) => setHint(e.target.value)} className={inputClass} />
                    </div>

                    {/* Tags */}
                    <div className="flex flex-col gap-1">
                        <label htmlFor="card-tags" className="text-gray-300 text-sm">Tags</label>
                        <input id="card-tags" type="text" value={tags} onChange={(e) => setTags(e.target.value)} className={inputClass} placeholder="java, basics" />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-[#2A2B32] transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isSubmitting}
                        onClick={(e) => {
                            e.preventDefault();
                            onSubmit({ title: front, deadline: back, repeat: hint, tags });
                        }}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-500 transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSubmitting ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
AddRecallCardModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialFront: PropTypes.string,
    initialBack: PropTypes.string,
    initialHint: PropTypes.string,
    initialTags: PropTypes.string,
    title: PropTypes.string,
};
